import React, { useEffect, useState } from "react";
import { Zap, Plus, Loader2, Trash2, Pencil, X, Check } from "lucide-react";
import PageHeader from "@/components/admin/PageHeader";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

const empty = { title: "", text: "" };

export default function SettingsCannedReplies() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(empty);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await adminApi.get("/api/admin/canned-replies");
            setItems(data.items || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const openCreate = () => {
        setForm(empty);
        setEditingId(null);
        setShowForm(true);
    };
    const openEdit = (c) => {
        setForm({ title: c.title, text: c.text });
        setEditingId(c.id);
        setShowForm(true);
    };

    const save = async () => {
        if (!form.title.trim() || !form.text.trim()) {
            toast.error("Title and text are required");
            return;
        }
        setSaving(true);
        try {
            if (editingId) {
                await adminApi.put(`/api/admin/canned-replies/${editingId}`, form);
                toast.success("Reply updated");
            } else {
                await adminApi.post("/api/admin/canned-replies", form);
                toast.success("Reply added");
            }
            setShowForm(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (c) => {
        if (!window.confirm(`Delete "${c.title}"?`)) return;
        try {
            await adminApi.delete(`/api/admin/canned-replies/${c.id}`);
            toast.success("Reply deleted");
            load();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    const inputCls =
        "w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15";

    return (
        <div className="p-10 max-w-3xl" data-testid="page-settings-canned">
            <PageHeader
                eyebrow="Settings"
                title="Canned Replies"
                description="One-tap saved responses your live agents can drop into a chat instantly. Manage the reusable answers for rates, check-in, directions and more."
            />

            <div className="flex justify-end mb-4">
                <button
                    onClick={openCreate}
                    data-testid="add-canned-btn"
                    className="inline-flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-4 py-2 rounded-md"
                >
                    <Plus size={15} /> Add reply
                </button>
            </div>

            {showForm && (
                <div className="bg-white border border-orchid-700/30 rounded-xl p-5 mb-6 shadow-card" data-testid="canned-form">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-medium text-stone-900">{editingId ? "Edit reply" : "New reply"}</h3>
                        <button onClick={() => setShowForm(false)} className="text-stone-400 hover:text-stone-600">
                            <X size={16} />
                        </button>
                    </div>
                    <label className="text-[11px] uppercase tracking-wider text-stone-500 block mb-1.5">Title (shown to agents)</label>
                    <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="canned-title" className={inputCls} placeholder="e.g. Room rates" />
                    <label className="text-[11px] uppercase tracking-wider text-stone-500 block mb-1.5 mt-4">Reply text</label>
                    <textarea rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="canned-text" className={inputCls} placeholder="The message the agent will send…" />
                    <div className="flex justify-end gap-2 mt-5">
                        <button onClick={() => setShowForm(false)} className="text-sm px-4 py-2 rounded-md border border-stone-200 text-stone-600 hover:bg-stone-50">
                            Cancel
                        </button>
                        <button onClick={save} disabled={saving} data-testid="canned-save" className="inline-flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-4 py-2 rounded-md disabled:opacity-50">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                            {editingId ? "Save changes" : "Add reply"}
                        </button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="p-8 flex items-center gap-2 text-stone-400 text-sm">
                    <Loader2 size={15} className="animate-spin" /> Loading…
                </div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-stone-200 rounded-xl p-10 text-center text-stone-400">
                    <Zap size={32} strokeWidth={1.2} className="mx-auto mb-3" />
                    <p className="text-sm">No canned replies yet. Add your first one-tap answer.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map((c) => (
                        <div key={c.id} className="bg-white border border-stone-200 rounded-xl p-4 flex items-start justify-between gap-3" data-testid={`canned-row-${c.id}`}>
                            <div className="min-w-0">
                                <div className="font-medium text-stone-900 flex items-center gap-1.5">
                                    <Zap size={13} className="text-orchid-700" /> {c.title}
                                </div>
                                <p className="text-sm text-stone-500 mt-1">{c.text}</p>
                            </div>
                            <div className="flex gap-2 shrink-0">
                                <button onClick={() => openEdit(c)} data-testid={`edit-canned-${c.id}`} className="text-stone-400 hover:text-orchid-700">
                                    <Pencil size={15} />
                                </button>
                                <button onClick={() => remove(c)} data-testid={`delete-canned-${c.id}`} className="text-stone-400 hover:text-red-600">
                                    <Trash2 size={15} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
